import { useEffect, useMemo, useRef, useState } from 'react'
import { Patient } from '@medplum/fhirtypes'
import { Button } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'

import { ArtifactsView } from '@/pages/Assistant/components/ArtifactsView'
import {
  partitionArtifacts,
  sectionAnchorId,
} from '@/pages/Assistant/components/artifacts-view.utils'
import { usePatientClinicalSummaryDetails } from '@/hooks/usePatientClinicalSummaryDetails'
import { ClinicalSummaryDetailsNav } from './ClinicalSummaryDetailsNav'
import { ClinicalSummaryLimitations } from './ClinicalSummaryLimitations'

type Props = {
  patient: Patient
}

// Sections whose first artifact sits at or above this line (below the scroll container's top)
// count as "reached" — the last reached one is the nav's active entry.
const SCROLL_OFFSET_PX = 16

// Below this container width the section rail is hidden entirely — covers the narrow call-page
// embedding, small viewports, and any future narrow layout without depending on the route. The
// rail itself is ~220px, and the artifact tables/charts need roughly 580px to stay readable.
const NAV_HIDE_BELOW_PX = 800

// A top-level section (the first element of the artifacts' `group` paths), in emission order —
// the converter controls section order; the UI must not impose its own. `anchor` is the DOM id of
// the root's first rendered section, `count` the artifacts under it (all sub-paths included).
type TopLevelSection = { root: string; anchor: string; count: number }

/**
 * The Details tab renders the SAME artifacts the assistant renders (the endpoint returns them
 * pre-built with `group` section paths), through the same ArtifactsView — plus the incomplete-data
 * notice and a right-side section rail that scrolls to (and tracks) the top-level sections.
 */
export const ClinicalSummaryDetails = ({ patient }: Props) => {
  const { data, loading, error, refresh } = usePatientClinicalSummaryDetails(patient.id)
  const [navCollapsed, setNavCollapsed] = useState(false)
  const [narrow, setNarrow] = useState(false)
  const [activeRoot, setActiveRoot] = useState<string | null>(null)
  // State (not a ref): the wrapper only mounts once data has loaded, so the observer effect must
  // re-run when the element actually attaches — a plain ref would leave it observing nothing.
  const [wrapperEl, setWrapperEl] = useState<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const artifacts = data?.artifacts
  const topLevelSections = useMemo<TopLevelSection[]>(() => {
    if (!artifacts) return []
    const roots: TopLevelSection[] = []
    for (const section of partitionArtifacts(artifacts).sections) {
      // The pill counts ITEMS, not artifacts: every chart and KPI is one item, and a table
      // contributes one per row (e.g. the Problems pill shows the number of problems).
      const count =
        section.kpis.length +
        section.charts.length +
        section.tables.reduce((sum, table) => sum + table.rows.length, 0)
      const existing = roots.find((entry) => entry.root === section.root)
      if (existing) existing.count += count
      else roots.push({ root: section.root, anchor: sectionAnchorId(section.label), count })
    }
    return roots
  }, [artifacts])

  // Hide the section rail responsively (covers the call-page embedding, where the tab is narrow).
  useEffect(() => {
    if (!wrapperEl) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (typeof width === 'number') setNarrow(width < NAV_HIDE_BELOW_PX)
    })
    observer.observe(wrapperEl)
    return () => observer.disconnect()
  }, [wrapperEl])

  // Scroll-spy over the rendered sections: the active nav entry is the last top-level section
  // whose first artifact has crossed the offset line.
  useEffect(() => {
    const container = scrollRef.current
    if (!container || !data) return
    const onScroll = () => {
      const containerTop = container.getBoundingClientRect().top
      let current: string | null = null
      for (const el of container.querySelectorAll<HTMLElement>('[data-section-root]')) {
        if (el.getBoundingClientRect().top - containerTop <= SCROLL_OFFSET_PX + 1) {
          current = el.dataset.sectionRoot ?? current
        }
      }
      setActiveRoot(current)
    }
    onScroll()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [data])

  if (loading && !data) {
    return <p className="py-12 text-center text-[#7D7987]">Loading clinical summary...</p>
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-red-600">{error}</p>
        <Button variant="default" onClick={refresh} leftSection={<IconRefresh size={14} />}>
          Retry
        </Button>
      </div>
    )
  }

  if (!data) return null

  const scrollToRoot = (root: string) => {
    const anchor = topLevelSections.find((entry) => entry.root === root)?.anchor
    if (!anchor) return
    scrollRef.current
      ?.querySelector(`#${anchor}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div ref={setWrapperEl} className="flex min-h-0 flex-1 gap-6">
      <div ref={scrollRef} className="min-w-0 flex-1 overflow-y-auto pb-4">
        <ClinicalSummaryLimitations meta={data.meta} />
        <div className="flex flex-col gap-3">
          <ArtifactsView artifacts={data.artifacts} showSuggestions={false} />
        </div>
      </div>

      {!narrow && (
        <ClinicalSummaryDetailsNav
          sections={topLevelSections.map(({ root, count }) => ({
            id: root,
            label: root,
            count,
          }))}
          activeId={activeRoot}
          collapsed={navCollapsed}
          onToggleCollapsed={() => setNavCollapsed((value) => !value)}
          onSelectSection={scrollToRoot}
        />
      )}
    </div>
  )
}
